import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { useCalls, useIncidents, usePortalClient } from '@ngsos/portal-core/portal';

import { AgencyView } from '@/app/shell/AgencyConfigurationProvider';
import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import { LoadingFailedBanner, LoadingFailedBox, NgButton } from '@/ui';
import { ATE_AGENCY_ID } from '@/features/shared/knownAgencyIds';
import { getQueryParamCaseInsensitive, selectIncidentMapPoints, toRow } from '@ngsos/portal-core/incidents';
import type { IncidentListContext } from './incidentListContext';
import { IncidentListLayoutBox } from './IncidentListLayoutBox';
import { IncidentListContent } from './IncidentListContent';
import { IncidentListMapContent } from './IncidentListMapContent';
import { NewIncidentDialog } from './NewIncidentDialog';
import type { NewIncidentDialogHandle } from './NewIncidentDialog';
import { NewIncidentFromDataSentenceDialog } from './NewIncidentFromDataSentenceDialog';
import type { NewIncidentFromDataSentenceDialogHandle } from './NewIncidentFromDataSentenceDialog';

export function IncidentListPage() {
  const { t } = useTranslation();
  const agencyConfiguration = useAgencyConfiguration();
  const [searchParams] = useSearchParams();

  const defaultCountryCode = (agencyConfiguration.agencyPortalConfiguration.defaultCountryCode as string | null) ?? '';

  const [context, setContext] = useState<IncidentListContext>('incidents');

  const newIncidentDialogRef = useRef<NewIncidentDialogHandle>(null);
  const newIncidentFromDataSentenceDialogRef = useRef<NewIncidentFromDataSentenceDialogHandle>(null);

  const portalClient = usePortalClient();
  const { liveIncidents: incidents, dataState } = useIncidents();
  const { calls: callNotifications } = useCalls();

  const isLoading = dataState === 'loading';
  const dataLoadFailed = dataState === 'failed';
  const hasLoadedData = incidents.length > 0 || callNotifications.length > 0;

  const retryDataLoad = async () => {
    await portalClient?.reload();
  };

  const requestedIncidentIdParam = getQueryParamCaseInsensitive(searchParams, 'requestedIncidentId');
  const phoneNumberParam = getQueryParamCaseInsensitive(searchParams, 'phoneNumber');
  const dialogOpenedFromQueryRef = useRef(false);

  useEffect(() => {
    if (isLoading || dialogOpenedFromQueryRef.current || requestedIncidentIdParam == null) {
      return;
    }

    dialogOpenedFromQueryRef.current = true;

    let phoneNumber = phoneNumberParam?.trim() ?? '';
    if (!phoneNumber.startsWith('+')) {
      phoneNumber = `${defaultCountryCode}${phoneNumber}`;
    }

    newIncidentDialogRef.current?.showModal({
      requestedIncidentId: requestedIncidentIdParam,
      phoneNumber,
    });
  }, [isLoading, requestedIncidentIdParam, phoneNumberParam, defaultCountryCode]);

  const createNewIncident = () => {
    newIncidentDialogRef.current?.showModal();
  };

  const showNewIncidentFromDataSentenceModal = () => {
    newIncidentFromDataSentenceDialogRef.current?.showModal();
  };

  const incidentMapPoints = selectIncidentMapPoints(incidents)?.map(toRow);

  return (
    <>
      <IncidentListLayoutBox
        bannersArea={dataLoadFailed && hasLoadedData ? <LoadingFailedBanner onRetry={retryDataLoad} /> : null}
        incidentListArea={
          dataLoadFailed && !hasLoadedData ? (
            <LoadingFailedBox onRetry={retryDataLoad} />
          ) : (
            <IncidentListContent
              isLoading={isLoading}
              onTabSwitch={setContext}
              incidents={incidents}
              callNotifications={callNotifications}
            />
          )
        }
        actionBarArea={
          <div className="d-flex flex-column flex-tablet-row gap-2 align-items-stretch">
            <NgButton text={t('Button_NewIncident')} icon="fal fa-plus-circle" onClick={createNewIncident} />

            <AgencyView agencyId={ATE_AGENCY_ID}>
              <NgButton
                text={t('Button_NewIncidentFromDataSentence')}
                icon="fal fa-plus-circle"
                onClick={showNewIncidentFromDataSentenceModal}
              />
            </AgencyView>
          </div>
        }
        mapArea={
          <IncidentListMapContent
            context={context}
            incidents={incidentMapPoints}
            sourceIncidents={incidents}
            callNotifications={callNotifications}
          />
        }
      />

      <NewIncidentDialog ref={newIncidentDialogRef} />
      <NewIncidentFromDataSentenceDialog ref={newIncidentFromDataSentenceDialogRef} />
    </>
  );
}
